import { StringSchema } from "../../classes/schema";
import { TranslationKey } from "../../functions/translate";
import { mbiRegex } from "../../functions/isValidMedicareNumber";

const noSpaces = /^[^\s]+$/;
const validSymbols = '@!._$\\-';
const symbols = new RegExp(`[${validSymbols}]`);
// spaces are not valid, but are included here because spaces are covered in a different requirement
const validCharsAndSpaces = new RegExp(`^[${validSymbols}a-z0-9 ]*$`, 'i');

export type UsernameErrState =
    'invalidLengthOrSpace' |
    'invalidSymbol' |
    'containsMBI';

const errorMessages: {
    [err in UsernameErrState]: TranslationKey
} = {
    invalidLengthOrSpace: 'enter_username_eight_to_thirty_char',
    invalidSymbol: "enter_username_symbol_first_or_last",
    containsMBI: 'enter_username_no_mbi'
}

const usernameSchema = new StringSchema()
    .matches(noSpaces, errorMessages.invalidLengthOrSpace)
    .betweenLength(8, 30, errorMessages.invalidLengthOrSpace)
    .test('at least 4 letters', errorMessages.invalidLengthOrSpace, username => {
        const characters = username?.split('');
        if (characters) {
            const letters = characters.filter(x => x.match(/[a-z]/i));
            return letters.length >= 4;
        }
        return true;
    })
    .matches(validCharsAndSpaces, errorMessages.invalidSymbol)
    .test('cannot begin or end with special character', errorMessages.invalidSymbol, username => {
        const characters = username?.split('');
        if (characters && characters.length) {
            const first = characters[0];
            const last = characters.slice(-1)[0];
            return !first.match(symbols) && !last.match(symbols);
        }
        return true;
    })
    .test('no MBI', errorMessages.containsMBI, username => {
        if (username) {
            return Boolean(username.length && !username.match(mbiRegex));
        }
        return false;
    });

export const getUsernameErrors = (username: string): Set<UsernameErrState> => {
    const usernameErrs = usernameSchema.validate(username);
    const errSet = new Set<UsernameErrState>();

    for (const key in errorMessages) {
        const errType = key as UsernameErrState;
        const message = errorMessages[errType];
        if (usernameErrs?.includes(message)) {
            errSet.add(errType);
        }
    }

    return errSet;
};

export const getUsernameErrorMessage = (err: UsernameErrState): TranslationKey => errorMessages[err];